import { importI18n as importSearchAiI18n } from '@atlassian/search-ai/i18n';
import { importI18n as importSearchCommonI18n } from '@atlassian/search-common/i18n';
import { importI18n as importSearchUiI18n } from '@atlassian/search-ui/i18n';

type MessageFile = {
	default: { [key: string]: string };
};

/**
 * Tries to get the most specific messages bundle for a given locale.
 *
 * Strategy:
 * 1. Try to find messages with the exact string (i.e. 'fr_FR')
 * 2. If that doesn't work, try to find messages for the country locale (i.e. 'fr')
 * 3. If that doesn't work, return english messages as a fallback.
 *
 * @param locale string specifying the locale like 'en_GB', or 'fr'.
 */
export const getSearchPageMessagesForLocale = async (locale: string): Promise<MessageFile> => {
	switch (locale) {
		case 'cs': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-cs" */ '../../i18n/cs'
			);
		}
		case 'da': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-da" */ '../../i18n/da'
			);
		}
		case 'de': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-de" */ '../../i18n/de'
			);
		}
		case 'en': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-en" */ '../../i18n/en'
			);
		}
		case 'en_GB': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-en_GB" */ '../../i18n/en_GB'
			);
		}
		case 'en_ZZ': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-en_ZZ" */ '../../i18n/en_ZZ'
			);
		}
		case 'es': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-es" */ '../../i18n/es'
			);
		}
		case 'fi': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-fi" */ '../../i18n/fi'
			);
		}
		case 'fr': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-fr" */ '../../i18n/fr'
			);
		}
		case 'hr': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-hr" */ '../../i18n/hr'
			);
		}
		case 'hu': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-hu" */ '../../i18n/hu'
			);
		}
		case 'it': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-it" */ '../../i18n/it'
			);
		}
		case 'ja': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-ja" */ '../../i18n/ja'
			);
		}
		case 'ko': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-ko" */ '../../i18n/ko'
			);
		}
		case 'nb': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-nb" */ '../../i18n/nb'
			);
		}
		case 'nl': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-nl" */ '../../i18n/nl'
			);
		}
		case 'pl': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-pl" */ '../../i18n/pl'
			);
		}
		case 'pt_BR':
		case 'pt-BR': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-pt_BR" */ '../../i18n/pt_BR'
			);
		}
		case 'ru': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-ru" */ '../../i18n/ru'
			);
		}
		case 'sl': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-sl" */ '../../i18n/sl'
			);
		}
		case 'sr_RS': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-sr_RS" */ '../../i18n/sr_RS'
			);
		}
		case 'sr_YR': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-sr_YR" */ '../../i18n/sr_YR'
			);
		}
		case 'sv': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-sv" */ '../../i18n/sv'
			);
		}
		case 'th': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-th" */ '../../i18n/th'
			);
		}
		case 'tr': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-tr" */ '../../i18n/tr'
			);
		}
		case 'uk': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-uk" */ '../../i18n/uk'
			);
		}
		case 'vi': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-vi" */ '../../i18n/vi'
			);
		}
		case 'zh_TW': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-zh_TW" */ '../../i18n/zh_TW'
			);
		}
		case 'zh': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-zh" */ '../../i18n/zh'
			);
		}
		default: {
			const fragments = locale.split(/[_-]/);
			if (fragments.length === 2) {
				return getSearchPageMessagesForLocale(fragments[0]);
			} else {
				return import(
					/* webpackChunkName: "@atlaskit-internal_search-page-i18n-en" */ '../../i18n/en'
				);
			}
		}
	}
};

/**
 * Tries to get the most specific messages bundle for a given locale.
 *
 * Strategy:
 * 1. Try to find messages with the exact string (i.e. 'fr_FR')
 * 2. If that doesn't work, try to find messages for the country locale (i.e. 'fr')
 * 3. If that doesn't work, return english messages as a fallback.
 *
 * @param locale string specifying the locale like 'en_GB', or 'fr'.
 */
export const getSearchCommonMessagesForLocale = async (locale: string): Promise<MessageFile> => {
	switch (locale) {
		case 'cs': {
			return importSearchCommonI18n('cs');
		}
		case 'da': {
			return importSearchCommonI18n('da');
		}
		case 'de': {
			return importSearchCommonI18n('de');
		}
		case 'en': {
			return importSearchCommonI18n('en');
		}
		case 'en_GB': {
			return importSearchCommonI18n('en_GB');
		}
		case 'en_ZZ': {
			return importSearchCommonI18n('en_ZZ');
		}
		case 'es': {
			return importSearchCommonI18n('es');
		}
		case 'fi': {
			return importSearchCommonI18n('fi');
		}
		case 'fr': {
			return importSearchCommonI18n('fr');
		}
		case 'hr': {
			return importSearchCommonI18n('hr');
		}
		case 'hu': {
			return importSearchCommonI18n('hu');
		}
		case 'it': {
			return importSearchCommonI18n('it');
		}
		case 'ja': {
			return importSearchCommonI18n('ja');
		}
		case 'ko': {
			return importSearchCommonI18n('ko');
		}
		case 'nb': {
			return importSearchCommonI18n('nb');
		}
		case 'nl': {
			return importSearchCommonI18n('nl');
		}
		case 'pl': {
			return importSearchCommonI18n('pl');
		}
		case 'pt_BR':
		case 'pt-BR': {
			return importSearchCommonI18n('pt_BR');
		}
		case 'ru': {
			return importSearchCommonI18n('ru');
		}
		case 'sl': {
			return importSearchCommonI18n('sl');
		}
		case 'sr_RS': {
			return importSearchCommonI18n('sr_RS');
		}
		case 'sr_YR': {
			return importSearchCommonI18n('sr_YR');
		}
		case 'sv': {
			return importSearchCommonI18n('sv');
		}
		case 'th': {
			return importSearchCommonI18n('th');
		}
		case 'tr': {
			return importSearchCommonI18n('tr');
		}
		case 'uk': {
			return importSearchCommonI18n('uk');
		}
		case 'vi': {
			return importSearchCommonI18n('vi');
		}
		case 'zh_TW': {
			return importSearchCommonI18n('zh_TW');
		}
		case 'zh': {
			return importSearchCommonI18n('zh');
		}
		default: {
			const fragments = locale.split(/[_-]/);
			if (fragments.length === 2) {
				return getSearchCommonMessagesForLocale(fragments[0]);
			} else {
				return importSearchCommonI18n('en');
			}
		}
	}
};

/**
 * Tries to get the most specific messages bundle for a given locale.
 *
 * Strategy:
 * 1. Try to find messages with the exact string (i.e. 'fr_FR')
 * 2. If that doesn't work, try to find messages for the country locale (i.e. 'fr')
 * 3. If that doesn't work, return english messages as a fallback.
 *
 * @param locale string specifying the locale like 'en_GB', or 'fr'.
 */
export const getSearchUiMessagesForLocale = async (locale: string): Promise<MessageFile> => {
	switch (locale) {
		case 'cs': {
			return importSearchUiI18n('cs');
		}
		case 'da': {
			return importSearchUiI18n('da');
		}
		case 'de': {
			return importSearchUiI18n('de');
		}
		case 'en': {
			return importSearchUiI18n('en');
		}
		case 'en_GB': {
			return importSearchUiI18n('en_GB');
		}
		case 'en_ZZ': {
			return importSearchUiI18n('en_ZZ');
		}
		case 'es': {
			return importSearchUiI18n('es');
		}
		case 'fi': {
			return importSearchUiI18n('fi');
		}
		case 'fr': {
			return importSearchUiI18n('fr');
		}
		case 'hr': {
			return importSearchUiI18n('hr');
		}
		case 'hu': {
			return importSearchUiI18n('hu');
		}
		case 'it': {
			return importSearchUiI18n('it');
		}
		case 'ja': {
			return importSearchUiI18n('ja');
		}
		case 'ko': {
			return importSearchUiI18n('ko');
		}
		case 'nb': {
			return importSearchUiI18n('nb');
		}
		case 'nl': {
			return importSearchUiI18n('nl');
		}
		case 'pl': {
			return importSearchUiI18n('pl');
		}
		case 'pt_BR':
		case 'pt-BR': {
			return importSearchUiI18n('pt_BR');
		}
		case 'ru': {
			return importSearchUiI18n('ru');
		}
		case 'sl': {
			return importSearchUiI18n('sl');
		}
		case 'sr_RS': {
			return importSearchUiI18n('sr_RS');
		}
		case 'sr_YR': {
			return importSearchUiI18n('sr_YR');
		}
		case 'sv': {
			return importSearchUiI18n('sv');
		}
		case 'th': {
			return importSearchUiI18n('th');
		}
		case 'tr': {
			return importSearchUiI18n('tr');
		}
		case 'uk': {
			return importSearchUiI18n('uk');
		}
		case 'vi': {
			return importSearchUiI18n('vi');
		}
		case 'zh_TW': {
			return importSearchUiI18n('zh_TW');
		}
		case 'zh': {
			return importSearchUiI18n('zh');
		}
		default: {
			const fragments = locale.split(/[_-]/);
			if (fragments.length === 2) {
				return getSearchUiMessagesForLocale(fragments[0]);
			} else {
				return importSearchUiI18n('en');
			}
		}
	}
};

/**
 * Tries to get the most specific messages bundle for a given locale.
 *
 * Strategy:
 * 1. Try to find messages with the exact string (i.e. 'fr_FR')
 * 2. If that doesn't work, try to find messages for the country locale (i.e. 'fr')
 * 3. If that doesn't work, return english messages as a fallback.
 *
 * @param locale string specifying the locale like 'en_GB', or 'fr'.
 */
export const getSearchAiMessagesForLocale = async (locale: string): Promise<MessageFile> => {
	switch (locale) {
		case 'cs': {
			return importSearchAiI18n('cs');
		}
		case 'da': {
			return importSearchAiI18n('da');
		}
		case 'de': {
			return importSearchAiI18n('de');
		}
		case 'en': {
			return importSearchAiI18n('en');
		}
		case 'en_GB': {
			return importSearchAiI18n('en_GB');
		}
		case 'en_ZZ': {
			return importSearchAiI18n('en_ZZ');
		}
		case 'es': {
			return importSearchAiI18n('es');
		}
		case 'fi': {
			return importSearchAiI18n('fi');
		}
		case 'fr': {
			return importSearchAiI18n('fr');
		}
		case 'hr': {
			return importSearchAiI18n('hr');
		}
		case 'hu': {
			return importSearchAiI18n('hu');
		}
		case 'it': {
			return importSearchAiI18n('it');
		}
		case 'ja': {
			return importSearchAiI18n('ja');
		}
		case 'ko': {
			return importSearchAiI18n('ko');
		}
		case 'nb': {
			return importSearchAiI18n('nb');
		}
		case 'nl': {
			return importSearchAiI18n('nl');
		}
		case 'pl': {
			return importSearchAiI18n('pl');
		}
		case 'pt_BR':
		case 'pt-BR': {
			return importSearchAiI18n('pt_BR');
		}
		case 'ru': {
			return importSearchAiI18n('ru');
		}
		case 'sl': {
			return importSearchAiI18n('sl');
		}
		case 'sr_RS': {
			return importSearchAiI18n('sr_RS');
		}
		case 'sr_YR': {
			return importSearchAiI18n('sr_YR');
		}
		case 'sv': {
			return importSearchAiI18n('sv');
		}
		case 'th': {
			return importSearchAiI18n('th');
		}
		case 'tr': {
			return importSearchAiI18n('tr');
		}
		case 'uk': {
			return importSearchAiI18n('uk');
		}
		case 'vi': {
			return importSearchAiI18n('vi');
		}
		case 'zh_TW': {
			return importSearchAiI18n('zh_TW');
		}
		case 'zh': {
			return importSearchAiI18n('zh');
		}
		default: {
			const fragments = locale.split(/[_-]/);
			if (fragments.length === 2) {
				return getSearchAiMessagesForLocale(fragments[0]);
			} else {
				return importSearchAiI18n('en');
			}
		}
	}
};

/**
 * Fetches the messages from all packages for a locale.
 *
 * @param locale string specifying the locale like 'en_GB', or 'fr'.
 */
export function fetchLanguageForPackages(locale: string): Promise<{ [key: string]: string }> {
	return Promise.allSettled([
		getSearchPageMessagesForLocale(locale),
		getSearchCommonMessagesForLocale(locale),
		getSearchUiMessagesForLocale(locale),
		getSearchAiMessagesForLocale(locale),
	]).then((results) => {
		const accumulatedMessages = results.reduce((acc, result) => {
			if (result.status === 'fulfilled') {
				return { ...acc, ...result.value.default };
			}
			return acc;
		}, {});
		return accumulatedMessages;
	});
}
